import {Component, OnDestroy, OnInit} from '@angular/core';
import {finalize} from 'rxjs';
import {AdminService, BrevoExportJob} from '../admin.service';
import {NgForOf, NgIf} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslatorService} from '../../translation/translator.service';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faDownload} from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-brevo-export',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    FormsModule,
    FontAwesomeModule
  ],
  templateUrl: './brevo-export.component.html',
  styleUrl: './brevo-export.component.scss'
})
export class BrevoExportComponent implements OnInit, OnDestroy {
  faDownload = faDownload;
  userCount: number = 0;
  isLoading: boolean = false;
  exportError: string | null = null;
  downloadError: string | null = null;
  downloadInfo: string | null = null;

  jobs: BrevoExportJob[] = [];
  isTriggering: boolean = false;
  isDeletingJobs: boolean = false;
  deleteJobsResult: string | null = null;
  deleteJobsError: string | null = null;
  pollingInterval: ReturnType<typeof setInterval> | null = null;

  resetDate: string = '';
  isResetting: boolean = false;
  resetResult: string | null = null;
  resetError: string | null = null;

  resetLastCount: number = 500;
  isResettingLast: boolean = false;
  resetLastResult: string | null = null;
  resetLastError: string | null = null;

  constructor(private adminService: AdminService,
              private translatorService: TranslatorService) { }

  ngOnInit(): void {
    this.loadUserCount();
    this.loadJobs();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  get latestJob(): BrevoExportJob | null {
    return this.jobs.length > 0 ? this.jobs[0] : null;
  }

  get isExportRunning(): boolean {
    return this.latestJob?.status === 'RUNNING';
  }

  loadUserCount(): void {
    this.isLoading = true;
    this.adminService.getNotExportedBrevoUsersCount()
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (count) => {
          this.userCount = count;
        },
        error: (error) => {
          console.error(this.__('error-count'), error);
        }
      });
  }

  loadJobs(): void {
    const wasRunning = this.isExportRunning;
    this.adminService.getBrevoExportJobs()
      .subscribe({
        next: (jobs) => {
          this.jobs = jobs;
          if (this.isExportRunning) {
            this.startPolling();
          }
          else {
            this.stopPolling();
            if (wasRunning) {
              this.loadUserCount();
            }
          }
        },
        error: (error) => {
          console.error('Failed to load export jobs', error);
        }
      });
  }

  triggerExport(): void {
    if (this.isTriggering || this.isExportRunning) {
      return;
    }

    this.isTriggering = true;
    this.exportError = null;

    this.adminService.triggerBrevoExport()
      .pipe(finalize(() => this.isTriggering = false))
      .subscribe({
        next: () => {
          this.loadJobs();
        },
        error: (error) => {
          if (error.status === 409) {
            this.exportError = this.__('already-running');
            this.loadJobs();
          }
          else {
            this.exportError = this.__('error-export') + ' ' + (error.message || error);
          }
        }
      });
  }

  downloadExport(job: BrevoExportJob): void {
    this.downloadError = null;
    this.downloadInfo = null;

    this.adminService.downloadBrevoExport(job.id)
      .subscribe({
        next: (response) => {
          if (response.status === 204) {
            this.downloadInfo = this.__('download-empty');
            return;
          }

          const blob = response.body;
          if (!blob || blob.size === 0) {
            this.downloadInfo = this.__('download-empty');
            return;
          }

          const filename = job.filename || 'brevo_export.csv';
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);
          }, 100);
        },
        error: (error) => {
          console.error('Download failed', error);
          this.downloadError = this.__('download-error') + ' ' + (error.message || error.status || error);
        }
      });
  }

  resetSince(): void {
    if (this.isResetting || !this.resetDate) {
      return;
    }

    this.isResetting = true;
    this.resetResult = null;
    this.resetError = null;

    this.adminService.resetBrevoExportSince(this.resetDate)
      .pipe(finalize(() => this.isResetting = false))
      .subscribe({
        next: (count) => {
          this.resetResult = this.__('reset-success').replace('{count}', String(count));
          this.loadUserCount();
        },
        error: (error) => {
          this.resetError = this.__('reset-error') + ' ' + (error.message || error);
        }
      });
  }

  resetLast(): void {
    if (this.isResettingLast || !this.resetLastCount || this.resetLastCount <= 0) {
      return;
    }

    this.isResettingLast = true;
    this.resetLastResult = null;
    this.resetLastError = null;

    this.adminService.resetBrevoExportLast(this.resetLastCount)
      .pipe(finalize(() => this.isResettingLast = false))
      .subscribe({
        next: (count) => {
          this.resetLastResult = this.__('reset-last-success').replace('{count}', String(count));
          this.loadUserCount();
        },
        error: (error) => {
          this.resetLastError = this.__('reset-last-error') + ' ' + (error.message || error);
        }
      });
  }

  deleteOldExports(): void {
    if (this.isDeletingJobs) {
      return;
    }

    this.isDeletingJobs = true;
    this.deleteJobsResult = null;
    this.deleteJobsError = null;

    this.adminService.deleteBrevoExportJobs()
      .pipe(finalize(() => this.isDeletingJobs = false))
      .subscribe({
        next: (response) => {
          this.deleteJobsResult = this.__('delete-jobs-success')
              .replace('{count}', String(response.deleted));
          this.loadJobs();
        },
        error: (error) => {
          this.deleteJobsError = this.__('delete-jobs-error') + ' ' + (error.message || error.status || error);
        }
      });
  }

  private startPolling(): void {
    if (this.pollingInterval) {
      return;
    }
    this.pollingInterval = setInterval(() => {
      this.loadJobs();
    }, 3000);
  }

  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  formatDate(dateStr: string | null): string {
    if (!dateStr) {
      return '';
    }
    return new Date(dateStr).toLocaleString();
  }

  __(key: string): string {
    return this.translatorService.translate('brevo-exports.' + key);
  }
}
